import { Cart } from '@app/types/monolith/cart';
import { UpdateShippingDetailsAPIRequest } from '@app/types/monolith/types';
import logger from '@redbubble/next/logger';
import { useRef, useState } from 'react';
import type { CartCheckoutClient } from '../../cartCheckoutClient';
import type { ErrorCodeType, StateDispatch } from '../types';
import { DispatchActionsType, ErrorCode } from '../types';
import { getUniqueProductTypeIds } from '../utils';
import {
  AsyncHookState,
  AsyncRequestResult,
  StateCode,
} from './AsyncHookState';

type HookState = AsyncHookState<ErrorCodeType, Cart>;
type AsyncResult = AsyncRequestResult<ErrorCodeType, Cart>;

export class UseUpdateShippingDetailsUnexpectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UseUpdateShippingDetailsUnexpectedError';
  }
}

export const useUpdateShippingDetails = ({
  client,
  dispatch,
}: {
  client: CartCheckoutClient;
  dispatch: StateDispatch;
}) => {
  const abortController = useRef<AbortController>();
  const [result, setResult] = useState<HookState>({});

  const handler = async ({
    state,
    postcode,
    countryCode,
  }: UpdateShippingDetailsAPIRequest): Promise<AsyncResult> => {
    // Abort any pending requests
    if (abortController.current) {
      abortController.current.abort();
    }

    const newAbortController = new AbortController();
    abortController.current = newAbortController;

    setResult((prev) => ({
      ...prev,
      state: StateCode.LOADING,
    }));
    dispatch({ type: DispatchActionsType.SET_CART_LOADING, payload: true });
    dispatch({
      type: DispatchActionsType.SET_DELIVERY_DATES_LOADING,
      payload: true,
    });

    return client
      .updateUserSettings(
        { country_code: countryCode },
        newAbortController.signal
      )
      .then(() => {
        return client.updateShippingDetails(
          {
            state,
            postcode,
            countryCode,
          },
          newAbortController.signal
        );
      })
      .then(async (res) => {
        dispatch({ type: DispatchActionsType.SET_CART, payload: res });

        return client
          .fetchDeliveryDates(
            {
              countryCode,
              productTypeIds: getUniqueProductTypeIds(res.items),
              fetchState: 'delivery-dates-fetch',
            },
            newAbortController.signal
          )
          .then((deliveryDates) => {
            dispatch({
              type: DispatchActionsType.SET_DELIVERY_DATES,
              payload: deliveryDates,
            });

            return {
              data: res,
              state: StateCode.SUCCESS,
            };
          });
      })
      .catch<AsyncResult>((err) => {
        const errMessage = err instanceof Error ? err.message : String(err);

        const fetchCartError = new UseUpdateShippingDetailsUnexpectedError(
          `Unexpected error while fetching cart using useUpdateShippingDetails() "${errMessage}"`
        );

        logger.error(
          {
            err: fetchCartError,
            message: fetchCartError.message,
            context: 'CartAndDeliveryDatesDataContext/useUpdateShippingDetails',
          },
          fetchCartError.message
        );

        dispatch({
          type: DispatchActionsType.SET_CART_LOADING,
          payload: false,
        });
        dispatch({
          type: DispatchActionsType.SET_DELIVERY_DATES_LOADING,
          payload: false,
        });

        return {
          data: undefined,
          state: StateCode.FAILED,
          errors: [
            {
              code: ErrorCode.SHIPPING_DETAILS_UPDATE_ERROR,
              exception: err,
            },
          ],
        };
      })
      .then((r) => {
        setResult(r);
        return r;
      });
  };

  return {
    ...result,
    abortController,
    handler,
  };
};
